import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem('access_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Track whether a refresh is already in progress to prevent race conditions
let _isRefreshing = false;
let _refreshQueue = [];

apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      const refreshToken = localStorage.getItem('refresh_token');
      if (!refreshToken) {
        localStorage.removeItem('access_token');
        localStorage.removeItem('last_activity');
        sessionStorage.setItem('logout_reason', 'expired');
        window.location.href = '/login';
        return Promise.reject(error);
      }
      if (_isRefreshing) {
        // Queue requests while a refresh is in progress
        return new Promise((resolve, reject) => {
          _refreshQueue.push({ resolve, reject });
        }).then((token) => {
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return apiClient(originalRequest);
        });
      }
      _isRefreshing = true;
      try {
        const response = await axios.post(`${API_BASE_URL}/auth/refresh`, null, {
          params: { refresh_token: refreshToken },
        });
        const { access_token, refresh_token: newRefresh } = response.data;
        localStorage.setItem('access_token', access_token);
        localStorage.setItem('refresh_token', newRefresh);
        localStorage.setItem('last_activity', Date.now().toString());
        originalRequest.headers.Authorization = `Bearer ${access_token}`;
        // Resolve queued requests
        _refreshQueue.forEach(({ resolve }) => resolve(access_token));
        _refreshQueue = [];
        return apiClient(originalRequest);
      } catch (refreshError) {
        _refreshQueue.forEach(({ reject }) => reject(refreshError));
        _refreshQueue = [];
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        localStorage.removeItem('last_activity');
        sessionStorage.setItem('logout_reason', 'expired');
        window.location.href = '/login';
        return Promise.reject(refreshError);
      } finally {
        _isRefreshing = false;
      }
    }
    return Promise.reject(error);
  }
);

export default apiClient;

// Auth API
export const authAPI = {
  register: (data) => apiClient.post('/auth/register', data),
  login: (data) => apiClient.post('/auth/login', data),
  socialLogin: (data) => apiClient.post('/auth/social-login', data),
  getMe: () => apiClient.get('/auth/me'),
  updateMe: (data) => apiClient.put('/auth/me', data),
  changePassword: (data) => apiClient.post('/auth/change-password', data),
  forgotPassword: (data) => apiClient.post('/auth/forgot-password', data),
  uploadAvatar: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return apiClient.post('/auth/me/upload-avatar', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
};

// Properties API
export const propertiesAPI = {
  list: (params) => apiClient.get('/properties/', { params }),
  getFeatured: () => apiClient.get('/properties/featured'),
  getById: (id) => apiClient.get(`/properties/${id}`),
  create: (data) => apiClient.post('/properties/', data),
  update: (id, data) => apiClient.put(`/properties/${id}`, data),
  delete: (id) => apiClient.delete(`/properties/${id}`),
  addImage: (id, data) => apiClient.post(`/properties/${id}/images`, data),
  uploadImages: (id, files) => {
    const formData = new FormData();
    files.forEach((file) => formData.append('files', file));
    return apiClient.post(`/properties/${id}/upload-images`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  deleteImage: (propertyId, imageId) => apiClient.delete(`/properties/${propertyId}/images/${imageId}`),
  approve: (id) => apiClient.post(`/properties/${id}/approve`),
  reject: (id, reason) => apiClient.post(`/properties/${id}/reject`, null, { params: { reason } }),
  getMyProperties: (params) => apiClient.get('/properties/owner/my-properties', { params }),
  // Room endpoints
  createRoom: (propertyId, data) => apiClient.post(`/properties/${propertyId}/rooms`, data),
  listRooms: (propertyId) => apiClient.get(`/properties/${propertyId}/rooms`),
  getRoom: (propertyId, roomId) => apiClient.get(`/properties/${propertyId}/rooms/${roomId}`),
  updateRoom: (propertyId, roomId, data) => apiClient.put(`/properties/${propertyId}/rooms/${roomId}`, data),
  deleteRoom: (propertyId, roomId) => apiClient.delete(`/properties/${propertyId}/rooms/${roomId}`),
  uploadRoomImages: (propertyId, roomId, files) => {
    const formData = new FormData();
    files.forEach((file) => formData.append('files', file));
    return apiClient.post(`/properties/${propertyId}/rooms/${roomId}/upload-images`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  deleteRoomImage: (propertyId, roomId, imageId) => apiClient.delete(`/properties/${propertyId}/rooms/${roomId}/images/${imageId}`),
};

// Bookings API
export const bookingsAPI = {
  create: (data) => apiClient.post('/bookings/', data),
  list: (params) => apiClient.get('/bookings/', { params }),
  getById: (id) => apiClient.get(`/bookings/${id}`),
  update: (id, data) => apiClient.put(`/bookings/${id}`, data),
  confirm: (id) => apiClient.post(`/bookings/${id}/confirm`),
  cancel: (id) => apiClient.post(`/bookings/${id}/cancel`),
  createPairing: (data) => apiClient.post('/bookings/pairing', data),
  getAvailablePairings: (params) => apiClient.get('/bookings/pairing/available', { params }),
  matchPairing: (id) => apiClient.post(`/bookings/pairing/${id}/match`),
};

// Payments API
export const paymentsAPI = {
  list: (params) => apiClient.get('/payments/', { params }),
  getById: (id) => apiClient.get(`/payments/${id}`),
  refund: (data) => apiClient.post('/payments/refund', data),
  payBooking: (bookingId, method = 'card') => apiClient.post(`/payments/pay-booking/${bookingId}`, null, { params: { payment_method: method } }),
  gatewayInfo: () => apiClient.get('/payments/gateway-info'),
  // Paystack
  paystackInit: (data) => apiClient.post('/payments/paystack/initialize', data),
  paystackVerify: (reference) => apiClient.get(`/payments/paystack/verify/${reference}`),
  // Stripe
  stripeCreateIntent: (data) => apiClient.post('/payments/stripe/create-intent', data),
  // Auto-billing
  toggleAutoBilling: (paymentId, data) => apiClient.put(`/payments/${paymentId}/auto-billing`, data),
  // Revenue analytics
  revenueByProperty: () => apiClient.get('/payments/revenue/by-property'),
  // Receipt
  getReceiptData: (paymentId) => apiClient.get(`/payments/${paymentId}/receipt-data`),
  downloadReceipt: (paymentId) => apiClient.get(`/payments/${paymentId}/receipt`, { responseType: 'blob' }),
};

// Tickets API
export const ticketsAPI = {
  create: (data) => apiClient.post('/tickets/', data),
  list: (params) => apiClient.get('/tickets/', { params }),
  getById: (id) => apiClient.get(`/tickets/${id}`),
  update: (id, data) => apiClient.put(`/tickets/${id}`, data),
  getMessages: (id) => apiClient.get(`/tickets/${id}/messages`),
  addMessage: (id, data) => apiClient.post(`/tickets/${id}/messages`, data),
  escalate: (id, data) => apiClient.post(`/tickets/${id}/escalate`, data),
};

// AI API
export const aiAPI = {
  chat: (data) => apiClient.post('/ai/chat', data),
  recommendProperties: (data) => apiClient.post('/ai/recommend-properties', data),
  generateTerms: (data) => apiClient.post('/ai/generate-tenancy-terms', data),
};

// Tenancy API
export const tenancyAPI = {
  create: (data) => apiClient.post('/tenancy/', data),
  list: (params) => apiClient.get('/tenancy/', { params }),
  getById: (id) => apiClient.get(`/tenancy/${id}`),
  sign: (id) => apiClient.post(`/tenancy/${id}/sign`),
  terminate: (id, reason) => apiClient.post(`/tenancy/${id}/terminate`, null, { params: { reason } }),
  generateTerms: (id) => apiClient.post(`/tenancy/${id}/generate-terms`),
  getTemplate: () => apiClient.get('/tenancy/template'),
  updateTemplate: (data) => apiClient.put('/tenancy/template', data),
  update: (id, data) => apiClient.put(`/tenancy/${id}`, data),
  bookingsWithoutAgreement: () => apiClient.get('/tenancy/bookings-without-agreement'),
};

// Notifications API
export const notificationsAPI = {
  list: (params) => apiClient.get('/notifications/', { params }),
  getUnreadCount: () => apiClient.get('/notifications/unread-count'),
  markAsRead: (id) => apiClient.put(`/notifications/${id}/read`),
  markAllAsRead: () => apiClient.put('/notifications/read-all'),
  delete: (id) => apiClient.delete(`/notifications/${id}`),
};

// KYC API
export const kycAPI = {
  getMyStatus: () => apiClient.get('/kyc/my-status'),
  submit: (formData) => apiClient.post('/kyc/submit', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
};

// Disputes API
export const disputesAPI = {
  create: (data) => apiClient.post('/disputes/', data),
  list: (params) => apiClient.get('/disputes/', { params }),
  getById: (id) => apiClient.get(`/disputes/${id}`),
  update: (id, data) => apiClient.put(`/disputes/${id}`, data),
  assign: (id, assignedTo) => apiClient.post(`/disputes/${id}/assign`, null, { params: { assigned_to: assignedTo } }),
  resolve: (id, resolution, amount) => apiClient.post(`/disputes/${id}/resolve`, null, { params: { resolution, resolution_amount: amount } }),
  getMessages: (id) => apiClient.get(`/disputes/${id}/messages`),
  addMessage: (id, data) => apiClient.post(`/disputes/${id}/messages`, data),
  uploadEvidence: (id, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return apiClient.post(`/disputes/${id}/upload-evidence`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  getTenantContext: () => apiClient.get('/disputes/tenant-context'),
};

// Reviews API
export const reviewsAPI = {
  create: (data) => apiClient.post('/reviews/', data),
  listByProperty: (propertyId, params) => apiClient.get(`/reviews/property/${propertyId}`, { params }),
  delete: (id) => apiClient.delete(`/reviews/${id}`),
};

// Pairing Quiz API
export const pairingQuizAPI = {
  submitQuiz: (data) => apiClient.post('/pairing-quiz/submit', data),
  getMyProfile: () => apiClient.get('/pairing-quiz/my-profile'),
  getMatches: () => apiClient.get('/pairing-quiz/matches'),
};

// Ghana Data API
export const ghanaAPI = {
  getUniversities: (params) => apiClient.get('/ghana/universities', { params }),
  getRegions: () => apiClient.get('/ghana/regions'),
  getCities: (params) => apiClient.get('/ghana/cities', { params }),
  getRegionCities: () => apiClient.get('/ghana/region-cities'),
  getInstitutionTypes: () => apiClient.get('/ghana/institution-types'),
};

// Chat API
export const chatAPI = {
  getConversations: () => apiClient.get('/chat/conversations'),
  createConversation: (data) => apiClient.post('/chat/conversations', data),
  getMessages: (conversationId, params) => apiClient.get(`/chat/conversations/${conversationId}/messages`, { params }),
  sendMessage: (conversationId, data) => apiClient.post(`/chat/conversations/${conversationId}/messages`, data),
  editMessage: (messageId, data) => apiClient.put(`/chat/messages/${messageId}`, data),
  deleteMessage: (messageId) => apiClient.delete(`/chat/messages/${messageId}`),
  reactToMessage: (messageId, data) => apiClient.post(`/chat/messages/${messageId}/react`, data),
  pinConversation: (conversationId) => apiClient.put(`/chat/conversations/${conversationId}/pin`),
  muteConversation: (conversationId) => apiClient.put(`/chat/conversations/${conversationId}/mute`),
  deleteConversation: (conversationId) => apiClient.delete(`/chat/conversations/${conversationId}`),
  setTyping: (data) => apiClient.post('/chat/typing', data),
  getTyping: (conversationId) => apiClient.get(`/chat/typing/${conversationId}`),
  searchMessages: (params) => apiClient.get('/chat/search', { params }),
  getChattableUsers: (params) => apiClient.get('/chat/users', { params }),
  getPermissions: () => apiClient.get('/chat/permissions'),
  createPermission: (data) => apiClient.post('/chat/permissions', data),
  updatePermission: (id, data) => apiClient.put(`/chat/permissions/${id}`, data),
  deletePermission: (id) => apiClient.delete(`/chat/permissions/${id}`),
  uploadFile: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return apiClient.post('/chat/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  heartbeat: () => apiClient.post('/chat/heartbeat'),
  goOffline: () => apiClient.post('/chat/offline'),
  getOnlineStatus: (userId) => apiClient.get(`/chat/online/${userId}`),
  getSettings: () => apiClient.get('/chat/settings'),
};

// Settings API
export const settingsAPI = {
  list: (params) => apiClient.get('/settings/', { params }),
  update: (key, data) => apiClient.put(`/settings/${key}`, data),
  seed: () => apiClient.post('/settings/seed'),
};

// Admin API
export const adminAPI = {
  getDashboard: () => apiClient.get('/admin/dashboard'),
  getAnalytics: (days) => apiClient.get('/admin/dashboard/analytics', { params: { days } }),
  listUsers: (params) => apiClient.get('/admin/users', { params }),
  createUser: (data) => apiClient.post('/admin/users', data),
  getUser: (id) => apiClient.get(`/admin/users/${id}`),
  updateUser: (id, data) => apiClient.put(`/admin/users/${id}`, data),
  deleteUser: (id) => apiClient.delete(`/admin/users/${id}`),
  activateUser: (id) => apiClient.put(`/admin/users/${id}/activate`),
  deactivateUser: (id) => apiClient.put(`/admin/users/${id}/deactivate`),
  changeUserRole: (id, role) => apiClient.put(`/admin/users/${id}/role`, null, { params: { new_role: role } }),
  resetUserPassword: (id) => apiClient.post(`/admin/users/${id}/reset-password`),
  uploadUserAvatar: (id, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return apiClient.post(`/admin/users/${id}/upload-avatar`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  listProperties: (params) => apiClient.get('/admin/properties', { params }),
  approveProperty: (id) => apiClient.put(`/admin/properties/${id}/approve`),
  rejectProperty: (id, reason) => apiClient.put(`/admin/properties/${id}/reject`, null, { params: { reason } }),
  listBookings: (params) => apiClient.get('/admin/bookings', { params }),
  cancelBooking: (id, reason) => apiClient.put(`/admin/bookings/${id}/cancel`, null, { params: { reason } }),
  listPayments: (params) => apiClient.get('/admin/payments', { params }),
  listTenancy: (params) => apiClient.get('/admin/tenancy', { params }),
  getTenancyDetail: (id) => apiClient.get(`/admin/tenancy/${id}`),
  confirmBooking: (id) => apiClient.put(`/admin/bookings/${id}/confirm`),
  listTickets: (params) => apiClient.get('/admin/tickets', { params }),
  updateTicketStatus: (id, status) => apiClient.put(`/admin/tickets/${id}/status`, null, { params: { status } }),
  assignTicket: (id, assignedTo) => apiClient.put(`/admin/tickets/${id}/assign`, null, { params: assignedTo ? { assigned_to: assignedTo } : {} }),
  resolveTicket: (id, resolution) => apiClient.put(`/admin/tickets/${id}/resolve`, null, { params: { resolution: resolution || 'Resolved by admin' } }),
  listPendingKYC: (params) => apiClient.get('/admin/kyc/pending', { params }),
  listAllKYC: (params) => apiClient.get('/admin/kyc/all', { params }),
  getKYCDetail: (id) => apiClient.get(`/admin/kyc/${id}`),
  approveKYC: (id) => apiClient.put(`/admin/kyc/${id}/approve`),
  rejectKYC: (id, reason) => apiClient.put(`/admin/kyc/${id}/reject`, null, { params: { reason } }),
  // Disputes
  listDisputes: (params) => apiClient.get('/admin/disputes', { params }),
  assignDispute: (id, assignedTo) => apiClient.post(`/disputes/${id}/assign`, null, { params: { assigned_to: assignedTo } }),
  resolveDispute: (id, resolution, amount) => apiClient.post(`/disputes/${id}/resolve`, null, { params: { resolution, resolution_amount: amount } }),
};
